import React, { useEffect, useMemo, useState } from 'react';
import Button from 'components/atoms/Button';
import ProgressBar from 'components/molecules/ProgressBar';
import PageWrapper from 'components/organisms/PageOutlines/PageWrapper';
import AlertMessage, { LAYOUT_OPTIONS } from 'components/utilities/AlertMessage';
import toaster from 'utils/toaster';
import { convertToCsv, download } from 'utils/export';
import useScrollSpy from 'hooks/useScrollSpy';
import { ALL_QUESTIONS_COUNT, SECTIONS, TOAST_CONFIG } from './constants';
import SectionsSidebar from './components/SectionsSidebar';
import ContextSection from './components/ContextSection';
import SectionSkeleton from './components/SectionSkeleton';
import aiContextService from './service';
import css from './page.css';

const EXPORT_FILE_NAME = 'ai_context.csv';
const EXPORT_FIELDS = ['Theme', 'Subject', 'Question', 'Description', 'Answer'];

// UTF-8 BOM so Excel opens non-ASCII text.
const UTF8_BOM = '\uFEFF';

const AiContext = () => {
    const [answers, setAnswers] = useState({});
    const [savedAnswers, setSavedAnswers] = useState({});
    const [maxLengths, setMaxLengths] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [openSections, setOpenSections] = useState(() =>
        SECTIONS.reduce((acc, section) => ({ ...acc, [section.id]: true }), {})
    );
    const { activeSectionId, setActiveSectionId, setSectionRef, setSentinelRef, scrollToSection } = useScrollSpy({
        sections: SECTIONS,
        enabled: !isLoading,
    });

    useEffect(() => {
        let cancelled = false;
        aiContextService
            .getContext()
            .then(loaded => {
                if (cancelled) return;
                setAnswers(loaded.answers);
                setSavedAnswers(loaded.answers);
                setMaxLengths(loaded.maxLengths);
            })
            .catch(() => {
                if (!cancelled) toaster.error('Failed to load saved AI context', TOAST_CONFIG);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const answeredBySectionId = useMemo(
        () =>
            SECTIONS.reduce((acc, section) => {
                acc[section.id] = section.questions.filter(q => (answers[q.id] || '').trim()).length;
                return acc;
            }, {}),
        [answers]
    );

    const answeredCount = useMemo(() => Object.values(answeredBySectionId).reduce((sum, count) => sum + count, 0), [
        answeredBySectionId,
    ]);

    const hasUnsavedChanges = useMemo(() => {
        const ids = new Set([...Object.keys(answers), ...Object.keys(savedAnswers)]);
        return Array.from(ids).some(id => (answers[id] || '') !== (savedAnswers[id] || ''));
    }, [answers, savedAnswers]);

    const handleChange = (id, value) => setAnswers(prev => ({ ...prev, [id]: value }));

    const handleToggleSection = id => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

    const handleExport = () => {
        const rows = SECTIONS.flatMap(section =>
            section.questions.map(question => [
                section.name,
                question.title,
                question.description,
                question.placeholder || '',
                answers[question.id] || '',
            ])
        );
        download(EXPORT_FILE_NAME, UTF8_BOM + convertToCsv(EXPORT_FIELDS, rows));
    };

    const handleSave = () => {
        if (isSaving || !hasUnsavedChanges) return;
        setIsSaving(true);
        aiContextService
            .saveAnswers(answers)
            .then(saved => {
                const normalized = saved || {};
                setAnswers(normalized);
                setSavedAnswers(normalized);
                toaster.success('AI context saved', TOAST_CONFIG);
            })
            .catch(err => {
                const message = err?.value || 'Failed to save AI context';
                toaster.error(message, TOAST_CONFIG);
            })
            .finally(() => setIsSaving(false));
    };

    const handleSectionFocus = e => {
        const anchor = e.target.closest('[data-section-id]');
        if (anchor?.dataset.sectionId) {
            setActiveSectionId(anchor.dataset.sectionId);
        }
    };

    const handleSidebarClick = id => {
        setOpenSections(prev => ({ ...prev, [id]: true }));
        scrollToSection(id);
    };

    const progressPercentage = (answeredCount / ALL_QUESTIONS_COUNT) * 100;

    return (
        <PageWrapper className={css.page} disablePadding>
            <div className={css.topBar}>
                <div className={css.titleBlock}>
                    <h1 className={css.pageTitle}>AI Context</h1>
                    <div className={css.subtitle}>
                        Provide context to the Singular AI agent so it understands your business and decisions.
                    </div>
                </div>
            </div>

            <div className={css.summaryBar}>
                <div className={css.progressGroup}>
                    <span className={css.progressLabel}>Progress</span>
                    <div className={css.progressBar}>
                        <ProgressBar
                            progressSections={[{ percentage: progressPercentage }]}
                            barHeight="6px"
                            barWidth="180px"
                            containerColor="var(--gray200)"
                            fillColor="var(--blue300)"
                        />
                    </div>
                    <span className={css.progressCount}>
                        {answeredCount}/{ALL_QUESTIONS_COUNT}
                    </span>
                </div>
                <div className={css.actions}>
                    <Button type="secondary" Icon="download" onClick={handleExport}>
                        Export
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleSave}
                        showSpinner={isSaving}
                        disabled={isSaving || isLoading || !hasUnsavedChanges}
                    >
                        Save
                    </Button>
                </div>
            </div>

            <div className={css.body}>
                <SectionsSidebar
                    sections={SECTIONS}
                    answeredBySectionId={answeredBySectionId}
                    activeSectionId={activeSectionId}
                    onSectionClick={handleSidebarClick}
                />

                <div className={css.content} onFocus={handleSectionFocus}>
                    <AlertMessage
                        level="info"
                        layout={LAYOUT_OPTIONS.SNACKBAR}
                        title="Configure your AI context"
                        text="Answer these questions to help Singular AI understand your marketing strategy. Your responses shape how the AI analyzes your data and generates insights."
                        className={css.banner}
                    />

                    {isLoading &&
                        SECTIONS.map(section => (
                            <SectionSkeleton key={section.id} questionsCount={section.questions.length} />
                        ))}

                    {!isLoading &&
                        SECTIONS.map(section => (
                            <ContextSection
                                key={section.id}
                                ref={setSectionRef(section.id)}
                                section={section}
                                answers={answers}
                                maxLengths={maxLengths}
                                isOpen={openSections[section.id]}
                                onToggle={handleToggleSection}
                                onChange={handleChange}
                            />
                        ))}
                    {!isLoading && <div ref={setSentinelRef} aria-hidden />}
                </div>
            </div>
        </PageWrapper>
    );
};

export default AiContext;
